import moment from 'moment';
import Modal from 'react-modal';
import classNames from 'classnames';
import React, { useState, useMemo } from 'react';

import DropDown from 'components/common/dropDown';
import Loading from 'components/common/loading/Loading';
import DatePicker from 'components/common/datepicker/DatePicker';

import InputWrapper from 'components/common/inputWrapper';

import { isObjectEmpty } from 'utils/object';
import { getRequiredErrorLabel } from 'utils/string';
import { getFormattedDate, sortByDate } from 'utils/date';
import { LeapfrogHistory } from 'interface/createUpdateForm';
import { DropdownOption } from 'components/common/dropDown/Dropdown';

Modal.setAppElement('#root');

interface Area {
  id: number;
  name: string;
}

type DesignationHistoryItem = LeapfrogHistory['designationAreaHistory'][number];

interface DesignationHistoryTouched {
  designation?: boolean;
  area?: boolean;
  transitionDate?: boolean;
}

interface AddDesignationModalProps {
  values: {
    joinDate?: string | Date;
    designationAreaHistory?: DesignationHistoryItem[];
  };
  touched: {
    designationAreaHistory?: DesignationHistoryTouched | DesignationHistoryTouched[] | boolean;
  };
  handleChange: (event: { target: { name: string; value: DesignationHistoryItem[] | DesignationHistoryItem } }) => void;
  handleBlur: (event: { target: { name: string } }) => void;
  isOpen: boolean;
  buttonLabel: string;
  setFieldTouched: (name: string, touched: boolean) => void;
  designations: Array<{ id: number; name: string }>;
  areas: Area[];
  editIndex?: number;
  onClose: () => void;
  isCreateForm: boolean;
}

interface DesignationModalErrors {
  designation?: string | false;
  area?: string | false;
  transitionDate?: string | false;
}

const getSelectedOption = (item: DropdownOption | readonly DropdownOption[] | null): DropdownOption | null => {
  if (!item || Array.isArray(item) || !('value' in item)) {
    return null;
  }

  return { label: item.label, value: item.value };
};

const buildInitialPayload = (
  joinDate: string | Date | undefined,
  isCreateForm: boolean
): Partial<DesignationHistoryItem> => ({
  transitionDate: isCreateForm ? joinDate : getFormattedDate(),
});

const AddDesignationModalContent: React.FC<AddDesignationModalProps> = (props) => {
  const {
    values,
    touched,
    handleChange,
    handleBlur,
    isOpen,
    buttonLabel,
    setFieldTouched,
    designations,
    areas,
    editIndex,
    onClose,
    isCreateForm,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const initialPayload = useMemo(
    () => buildInitialPayload(values.joinDate, isCreateForm),
    [isCreateForm, values.joinDate]
  );
  const basePayload = useMemo(
    () => (isNaN(+editIndex) ? initialPayload : values.designationAreaHistory?.[editIndex] || initialPayload),
    [editIndex, initialPayload, values.designationAreaHistory]
  );
  const [designationAreaPayload, setDesignationAreaPayload] = useState<Partial<DesignationHistoryItem>>(basePayload);

  const areaOptions = useMemo(() => areas.map((area) => ({ label: area.name, value: area.id })), [areas]);

  const designationOptions = useMemo(
    () => designations.map((designation) => ({ label: designation.name, value: designation.id })),
    [designations]
  );

  const getDefaultValue = () => {
    return {
      designation: !isObjectEmpty(designationAreaPayload.designation)
        ? { label: designationAreaPayload.designation.name, value: designationAreaPayload.designation.id }
        : null,
      area: !isObjectEmpty(designationAreaPayload.area)
        ? { label: designationAreaPayload.area.name, value: designationAreaPayload.area.id }
        : null,
    };
  };

  const error = useMemo<DesignationModalErrors>(() => {
    if (
      !touched.designationAreaHistory ||
      typeof touched.designationAreaHistory !== 'object' ||
      Array.isArray(touched.designationAreaHistory)
    ) {
      return {};
    }
    const designationHistoryTouched = touched.designationAreaHistory;

    const { designation, transitionDate, area } = designationAreaPayload;

    return {
      designation: designationHistoryTouched.designation && !designation && getRequiredErrorLabel('designation'),
      area: designationHistoryTouched.area && !area && getRequiredErrorLabel('area'),
      transitionDate:
        designationHistoryTouched.transitionDate && !transitionDate && getRequiredErrorLabel('transition date'),
    };
  }, [touched, designationAreaPayload]);

  const handleCancel = () => {
    setDesignationAreaPayload(initialPayload);
    setFieldTouched('designationAreaHistory', false);
    onClose();
  };

  const handleDesignationSave = () => {
    const { designation, transitionDate, area } = designationAreaPayload;

    if (!designation || !transitionDate || !area) {
      handleBlur({
        target: {
          name: 'designationAreaHistory.designation',
        },
      });
      handleBlur({
        target: {
          name: 'designationAreaHistory.transitionDate',
        },
      });
      handleBlur({
        target: {
          name: 'designationAreaHistory.area',
        },
      });

      return;
    }

    let value;

    if (!isNaN(editIndex)) {
      value = [...(values.designationAreaHistory || [])];
      value[editIndex] = designationAreaPayload;
    } else {
      value = [designationAreaPayload, ...(values.designationAreaHistory || [])];
    }

    sortByDate(value, 'transitionDate');

    setIsLoading(true);
    handleChange({
      target: {
        name: 'designationAreaHistory',
        value,
      },
    });
    setDesignationAreaPayload(initialPayload);
    setFieldTouched('designationAreaHistory', false);
    setIsLoading(false);
    onClose();
  };

  const updateDesignationAreaPayload: React.Dispatch<React.SetStateAction<Partial<DesignationHistoryItem>>> = (
    value
  ) => {
    setDesignationAreaPayload((previousPayload) => (typeof value === 'function' ? value(previousPayload) : value));
  };

  return (
    <Modal
      isOpen={isOpen}
      className="lf-modal"
      overlayClassName="lf-modal__overlay"
      bodyOpenClassName="overflow-hidden"
    >
      <div className="add-designation-modal">
        <p className="add-designation-modal__header">{buttonLabel === 'Add' ? 'Add New' : 'Edit'} Designation</p>
        <div className="add-designation-modal__form">
          <div className="add-designation-modal__form__full-width">
            <div className="add-designation-modal__form__full-width__space">
              <InputWrapper label="Designation" error={error.designation} isMandatory>
                <DropDown
                  hasError={Boolean(error.designation)}
                  value={getDefaultValue().designation}
                  onDropDownChange={(item) => {
                    const selectedItem = getSelectedOption(item);
                    if (!selectedItem) {
                      return;
                    }
                    const selectedDesignation = designations.find(
                      (designation) => designation.id === selectedItem.value
                    );
                    if (!selectedDesignation) {
                      return;
                    }
                    updateDesignationAreaPayload((payload) => ({ ...payload, designation: selectedDesignation }));
                  }}
                  onBlur={() => handleBlur({ target: { name: 'designationAreaHistory.designation' } })}
                  options={designationOptions}
                  isSearchable={true}
                  isDisabled={false}
                  customClassName="employee-form__dropdown"
                />
              </InputWrapper>
            </div>
            <div className="add-designation-modal__form__full-width__space">
              <InputWrapper label="Area" error={error.area} isMandatory>
                <DropDown
                  hasError={Boolean(error.area)}
                  isSearchable={true}
                  isDisabled={false}
                  value={getDefaultValue().area}
                  options={areaOptions}
                  onBlur={() => handleBlur({ target: { name: 'designationAreaHistory.area' } })}
                  onDropDownChange={(item) => {
                    const selectedItem = getSelectedOption(item);
                    if (!selectedItem) {
                      return;
                    }
                    const selectedArea = areas.find((area) => area.id === selectedItem.value);
                    if (!selectedArea) {
                      return;
                    }
                    updateDesignationAreaPayload((payload) => ({ ...payload, area: selectedArea }));
                  }}
                  customClassName="employee-form__dropdown"
                />
              </InputWrapper>
            </div>
          </div>
          <div className="add-designation-modal__form__full-width">
            <div className="add-designation-modal__form__full-width__space">
              <InputWrapper
                label="Transition Date"
                error={error.transitionDate}
                isMandatory
                className="employee-form__date"
              >
                <DatePicker
                  hasError={Boolean(error.transitionDate)}
                  date={designationAreaPayload.transitionDate ? moment(designationAreaPayload.transitionDate) : null}
                  onDateChange={(date) =>
                    updateDesignationAreaPayload((payload) => ({ ...payload, transitionDate: getFormattedDate(date) }))
                  }
                  onClose={() => handleBlur({ target: { name: 'designationAreaHistory.transitionDate' } })}
                />
              </InputWrapper>
            </div>
          </div>
        </div>
        <div className="add-designation-modal__bottom">
          <button
            className={classNames('btn btn--primary', { 'btn--loading': isLoading })}
            disabled={isLoading}
            onClick={handleDesignationSave}
          >
            <span className="btn__label">{buttonLabel} &amp; Close</span>
            {isLoading && <Loading />}
          </button>
          <button className="btn btn--outlined-grey ml-md-24" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

const AddDesignationModal: React.FC<AddDesignationModalProps> = (props) => {
  const modalKey =
    `${props.editIndex ?? 'new'}-${String(props.values.joinDate)}` +
    `-${props.values.designationAreaHistory?.length ?? 0}-${props.isOpen ? 'open' : 'closed'}`;

  return <AddDesignationModalContent key={modalKey} {...props} />;
};

export default AddDesignationModal;
